/**
 * CDX Cross-Sell Subscription v5
 *
 * Multi-select: multiple cross-sell products can be selected simultaneously.
 * All selected products are sent as bundle items alongside the main product.
 */

if (!customElements.get('cdx-cross-sell-subscription')) {
  class CdxCrossSellSubscription extends HTMLElement {
    #abortController = null;
    #selectedIds = new Set();

    connectedCallback() {
      this.#abortController = new AbortController();
      const signal = this.#abortController.signal;

      this._form = document.forms[this.getAttribute('form')];
      this._cards = this.querySelectorAll('[data-cdx-cs-card]');
      this._deselect = this.querySelector('[data-cdx-cs-deselect]');
      this._parentId = this.getAttribute('data-parent-id') || '';

      if (!this._form) return;

      // Duplicate-product detection: if a cross-sell points to the SAME
      // product as the parent, we want the native <variant-picker> and the
      // CDX variant pills to stay in sync — both should work, picking in one
      // updates the other. We keep references to wire up two-way binding
      // below. The native picker stays visible.
      this._duplicateProductCard = this._parentId
        ? this.querySelector(`[data-cdx-cs-card="${this._parentId}"]`)
        : null;
      this._nativeVariantPicker = null;
      if (this._duplicateProductCard) {
        const formId = this.getAttribute('form');
        this._nativeVariantPicker = document.querySelector(
          `variant-picker[form-id="${formId}"]`
        );

        // Native → CDX sync: when the native picker fires variant:change on
        // the form (after its async selectCombination resolves), reflect the
        // chosen variant in the CDX card. Only fires for in-product variant
        // changes (productChange=false). For Combined-Listings options like
        // a "3er Pack" entry that links to a separate product, the theme
        // does a full content swap with productChange=true — no variant:change
        // is dispatched and a fresh CDX instance connects on the new page.
        // Going through _applyCardStateFromInput (not _onVariantChange)
        // avoids re-triggering the CDX → native sync and looping.
        if (this._form) {
          this._form.addEventListener(
            'variant:change',
            (event) => {
              const variant = event.detail && event.detail.variant;
              if (!variant || !variant.id || !this._duplicateProductCard) return;
              const pill = this.querySelector(
                `input[data-cdx-cs-variant="${this._parentId}"][value="${variant.id}"]`
              );
              if (!pill || pill.checked) return;
              pill.checked = true;
              this._applyCardStateFromInput(pill);
            },
            { signal }
          );
        }
      }

      // Single delegated click handler for cards + info buttons
      this.addEventListener('click', (e) => {
        // Info button clicked → open modal, do nothing else
        const infoBtn = e.target.closest('[data-cdx-cs-modal-open]');
        if (infoBtn) {
          e.preventDefault();
          e.stopPropagation();
          this._openModal(infoBtn.dataset.cdxCsModalOpen);
          return;
        }

        // Ignore clicks inside expanded area
        if (e.target.closest('[data-cdx-cs-expanded]')) return;

        // Card click → toggle product
        const card = e.target.closest('[data-cdx-cs-card]');
        if (card) {
          this._toggleProduct(card.dataset.cdxCsCard);
        }
      }, { signal });

      // Modal close/overlay clicks (on document since modal lives on body)
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-cdx-cs-modal-close]')) {
          this._closeAllModals();
          return;
        }
        const overlay = e.target.closest('[data-cdx-cs-modal]');
        if (overlay && e.target === overlay) {
          this._closeAllModals();
        }
      }, { signal });

      // Checkbox change (direct click on checkbox label)
      this.querySelectorAll('[data-cdx-cs-product]').forEach((cb) => {
        cb.addEventListener('change', (e) => {
          e.stopPropagation();
          if (cb.checked) {
            this._selectProduct(cb.value);
          } else {
            this._deselectProduct(cb.value);
          }
        }, { signal });
      });

      // Escape key → close modal
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this._closeAllModals();
      }, { signal });

      // Variant pill change
      this.querySelectorAll('[data-cdx-cs-variant]').forEach((input) => {
        input.addEventListener('change', () => this._onVariantChange(input), { signal });
      });

      // Plan pill change
      this.querySelectorAll('[data-cdx-cs-plan]').forEach((input) => {
        input.addEventListener('change', () => this._onPlanChange(input.dataset.cdxCsPlan), { signal });
      });

      // Deselect all
      this._deselect?.addEventListener('click', () => this._deselectAll(), { signal });

      // Intercept form submit
      document.addEventListener('submit', (e) => this._onSubmitCapture(e), { capture: true, signal });
    }

    disconnectedCallback() {
      this.#abortController?.abort();
    }

    /* ──────────────────────────────────────────────
     * State
     * ────────────────────────────────────────────── */

    get isActive() {
      return this.#selectedIds.size > 0;
    }

    /** Returns array of all selected cross-sell items */
    getSelectedCrossSells() {
      const items = [];

      this.#selectedIds.forEach((productId) => {
        const card = this.querySelector(`[data-cdx-cs-card="${productId}"]`);
        if (!card) return;

        const variantId = card.dataset.variantId;
        const planInput = this.querySelector(
          `[data-cdx-cs-plan="${productId}"][data-variant-id="${variantId}"]:checked`
        );
        const sellingPlanId = planInput?.value || '';

        items.push({ productId, variantId, sellingPlanId });
      });

      return items;
    }

    /* ──────────────────────────────────────────────
     * UI: Product toggle (multi-select)
     * ────────────────────────────────────────────── */

    _toggleProduct(productId) {
      if (this.#selectedIds.has(productId)) {
        this._deselectProduct(productId);
      } else {
        this._selectProduct(productId);
      }
    }

    _selectProduct(productId) {
      this.#selectedIds.add(productId);

      // Check the checkbox
      const cb = this.querySelector(`[data-cdx-cs-product][value="${productId}"]`);
      if (cb) cb.checked = true;

      // Highlight card
      const card = this.querySelector(`[data-cdx-cs-card="${productId}"]`);
      card?.classList.add('is-active');

      // Show expanded area
      const expanded = this.querySelector(`[data-cdx-cs-expanded="${productId}"]`);
      expanded?.classList.add('is-visible');

      // Show deselect button
      this._deselect?.classList.add('is-visible');

      // Update savings
      this._onPlanChange(productId);
    }

    _deselectProduct(productId) {
      this.#selectedIds.delete(productId);

      // Uncheck checkbox
      const cb = this.querySelector(`[data-cdx-cs-product][value="${productId}"]`);
      if (cb) cb.checked = false;

      // Remove highlight
      const card = this.querySelector(`[data-cdx-cs-card="${productId}"]`);
      card?.classList.remove('is-active');

      // Hide expanded area
      const expanded = this.querySelector(`[data-cdx-cs-expanded="${productId}"]`);
      expanded?.classList.remove('is-visible');

      // Hide savings
      const savings = this.querySelector(`[data-cdx-cs-savings="${productId}"]`);
      savings?.classList.remove('is-visible');

      // Hide deselect if nothing selected
      if (!this.isActive) {
        this._deselect?.classList.remove('is-visible');
      }
    }

    _deselectAll() {
      // Copy set to avoid mutation during iteration
      [...this.#selectedIds].forEach((id) => this._deselectProduct(id));
    }

    /* ──────────────────────────────────────────────
     * UI: Product detail modal
     * ────────────────────────────────────────────── */

    _openModal(productId) {
      const overlay = this.querySelector(`[data-cdx-cs-modal="${productId}"]`);
      if (!overlay) return;

      // Move to document.body to escape any stacking context
      document.body.appendChild(overlay);
      // Force reflow before adding class for animation
      overlay.offsetHeight;
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    _closeAllModals() {
      document.querySelectorAll('[data-cdx-cs-modal].is-open').forEach((overlay) => {
        overlay.classList.remove('is-open');
        // Move back into component
        this.querySelector('.cdx-cs__products')?.appendChild(overlay);
      });
      document.body.style.overflow = '';
    }

    /* ──────────────────────────────────────────────
     * UI: Variant switching
     * ────────────────────────────────────────────── */

    _onVariantChange(input) {
      // Apply visual state to the CDX card from the pill data.
      this._applyCardStateFromInput(input);

      const productId = input.dataset.cdxCsVariant;
      const newVariantId = input.value;

      // CDX → native sync: when the user switches variant on the same-product
      // cross-sell, drive the native variant-picker too so the PDP (price,
      // image, URL) follows along. Not called from the native variant:change
      // listener, which uses _applyCardStateFromInput directly → no loop.
      if (this._nativeVariantPicker && String(productId) === String(this._parentId)) {
        // Sync form.id.value SYNCHRONOUSLY first. selectCombination() is
        // async (fetches the variant section), so without this a fast
        // Add-to-Cart click could submit the form with the stale variant id.
        if (this._form && this._form.elements && this._form.elements.id) {
          this._form.elements.id.value = newVariantId;
        }

        const optionValues = [
          input.dataset.option1,
          input.dataset.option2,
          input.dataset.option3,
        ].filter((v) => v != null && v !== '');
        if (optionValues.length > 0 && typeof this._nativeVariantPicker.selectCombination === 'function') {
          this._nativeVariantPicker.selectCombination({ optionValues, productChange: false });
        }
      }
    }

    /**
     * Pure DOM-update path: given a CDX variant pill <input>, apply the
     * resulting state to its card (dataset, price, unit price, plan
     * visibility, savings reset). Does NOT trigger the native sync — that's
     * the caller's job in _onVariantChange. Reused by the native
     * variant:change listener so picking in the native picker updates the
     * CDX card without bouncing back into _onVariantChange.
     */
    _applyCardStateFromInput(input) {
      const productId = input.dataset.cdxCsVariant;
      const newVariantId = input.value;
      const newPrice = input.dataset.priceFormatted;

      const card = this.querySelector(`[data-cdx-cs-card="${productId}"]`);
      if (card) {
        card.dataset.variantId = newVariantId;
        card.dataset.price = input.dataset.price;
      }

      const priceDisplay = this.querySelector(`[data-cdx-cs-price-display="${productId}"]`);
      if (priceDisplay && newPrice) priceDisplay.textContent = newPrice;

      // Update unit price (PAngV)
      const unitPriceDisplay = this.querySelector(`[data-cdx-cs-unit-price="${productId}"]`);
      const newUnitPrice = input.dataset.unitPriceFormatted;
      if (unitPriceDisplay) {
        if (newUnitPrice) {
          unitPriceDisplay.textContent = newUnitPrice;
          unitPriceDisplay.style.display = '';
        } else {
          unitPriceDisplay.style.display = 'none';
        }
      }

      const expandedArea = this.querySelector(`[data-cdx-cs-expanded="${productId}"]`);
      if (expandedArea) {
        expandedArea.querySelectorAll('[data-cdx-cs-variant-plans]').forEach((planArea) => {
          planArea.classList.toggle('is-visible', planArea.dataset.cdxCsVariantPlans === `${productId}-${newVariantId}`);
        });
      }

      const savingsEl = this.querySelector(`[data-cdx-cs-savings="${productId}"]`);
      if (savingsEl) savingsEl.classList.remove('is-visible');
    }

    /* ──────────────────────────────────────────────
     * UI: Plan & savings
     * ────────────────────────────────────────────── */

    _onPlanChange(productId) {
      const card = this.querySelector(`[data-cdx-cs-card="${productId}"]`);
      if (!card) return;

      const currentVariantId = card.dataset.variantId;
      const checkedPlan = this.querySelector(
        `[data-cdx-cs-plan="${productId}"][data-variant-id="${currentVariantId}"]:checked`
      );
      if (!checkedPlan) return;

      const savingsInCents = parseInt(checkedPlan.dataset.savings || '0', 10);
      const savingsEl = this.querySelector(`[data-cdx-cs-savings="${productId}"]`);
      const amountEl = savingsEl?.querySelector('[data-cdx-cs-savings-amount]');

      if (!savingsEl || !amountEl) return;

      if (savingsInCents > 0) {
        amountEl.textContent = this._formatMoney(savingsInCents);
        savingsEl.classList.add('is-visible');
      } else {
        savingsEl.classList.remove('is-visible');
      }
    }

    _formatMoney(cents) {
      return (cents / 100).toFixed(2).replace('.', ',') + ' \u20AC';
    }

    /* ──────────────────────────────────────────────
     * Form submit interception
     * ────────────────────────────────────────────── */

    _onSubmitCapture(event) {
      if (event.target !== this._form) return;

      const crossSells = this.getSelectedCrossSells();
      if (crossSells.length === 0) return; // No selection → normal flow

      event.preventDefault();
      event.stopImmediatePropagation();
      this._handleBundleAdd(crossSells);
    }

    async _handleBundleAdd(crossSells) {
      const form = this._form;

      const submitButtons = Array.from(form.elements).filter((el) => el.type === 'submit');
      submitButtons.forEach((btn) => btn.setAttribute('aria-busy', 'true'));

      const sectionsToBundle = ['variant-added'];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );

      // Main product item
      const formData = new FormData(form);
      const mainVariantId = parseInt(formData.get('id'));

      // Unique bundle ID to link parent + children in cart
      const bundleId = `cdx_${Date.now()}`;

      const mainItem = {
        id: mainVariantId,
        quantity: parseInt(formData.get('quantity') || '1', 10),
      };

      const mainSellingPlan = formData.get('selling_plan');
      if (mainSellingPlan) mainItem.selling_plan = parseInt(mainSellingPlan, 10);

      const properties = {};
      for (const [key, value] of formData.entries()) {
        if (key.startsWith('properties[') && value) {
          properties[key.slice(11, -1)] = value;
        }
      }
      // Tag as bundle parent (underscore = hidden from customer)
      properties['_cdx_bundle'] = bundleId;
      mainItem.properties = properties;

      // Parent product info for order tracking
      const parentTitle = this.getAttribute('data-parent-title') || '';
      const parentId = this.getAttribute('data-parent-id') || '';

      // Duplicate-product guard: if a cross-sell points to the SAME product
      // as the parent and is selected, the cross-sell card is the user's
      // chosen variant/plan combination. Skip the main item entirely –
      // adding it would always produce a duplicate (same product) and
      // relying on form.id is fragile because selectCombination() is async
      // (a fast Add-to-Cart click can race it). The cross-sell card's
      // dataset.variantId is updated synchronously by _onVariantChange, so
      // using it as the single source of truth is robust. Applies to both
      // subscription and one-time variants.
      const duplicateIndex = crossSells.findIndex(
        (cs) => String(cs.productId) === String(parentId)
      );

      // Build items array: main (unless overridden) + ALL cross-sells
      const items = [];
      if (duplicateIndex === -1) {
        items.push(mainItem);
      }

      crossSells.forEach((cs, idx) => {
        const crossItem = {
          id: parseInt(cs.variantId, 10),
          quantity: 1,
          properties: {
            '_cdx_bundle': bundleId,
            '_cdx_bundle_child': 'true',
            '_cdx_parent_product': parentTitle,
            '_cdx_parent_product_id': parentId,
          },
        };
        if (cs.sellingPlanId) {
          crossItem.selling_plan = parseInt(cs.sellingPlanId, 10);
        }

        // Duplicate cross-sell replaces the main item: take main's
        // quantity & properties, drop the child flag so it becomes the anchor.
        if (idx === duplicateIndex) {
          crossItem.quantity = mainItem.quantity;
          crossItem.properties = { ...mainItem.properties };
          delete crossItem.properties['_cdx_bundle_child'];
        }

        items.push(crossItem);
      });

      const payload = {
        items,
        sections: sectionsToBundle.join(','),
        sections_url: `${Shopify.routes.root}variants/${mainVariantId}`,
      };

      try {
        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(payload),
        });

        submitButtons.forEach((btn) => btn.removeAttribute('aria-busy'));
        const responseJson = await response.json();

        if (response.ok) {
          if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
            window.location.href = `${Shopify.routes.root}cart`;
            return;
          }

          const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
          cartContent.sections = responseJson.sections;

          form.dispatchEvent(new CustomEvent('variant:add', {
            bubbles: true,
            detail: { items: responseJson.hasOwnProperty('items') ? responseJson.items : [responseJson], cart: cartContent },
          }));

          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', cart: cartContent },
          }));
        } else {
          form.dispatchEvent(new CustomEvent('cart:error', {
            bubbles: true,
            detail: { error: responseJson.description },
          }));
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
      } catch (err) {
        submitButtons.forEach((btn) => btn.removeAttribute('aria-busy'));
        console.error('[CDX Cross-Sell] Cart add failed:', err);
        form.dispatchEvent(new CustomEvent('cart:error', {
          bubbles: true,
          detail: { error: 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.' },
        }));
      }
    }
  }

  customElements.define('cdx-cross-sell-subscription', CdxCrossSellSubscription);
}